import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils";

/** Gap below the trigger, matching Tailwind `mt-2`. */
const MENU_OFFSET_PX = 8;

/**
 * Viewport-fixed coordinates for a menu aligned to the trigger's right edge.
 * @param {HTMLElement | null} anchor
 * @returns {{ top: number, right: number } | null}
 */
function menuCoordsForAnchor(anchor) {
  if (!anchor) {
    return null;
  }
  const rect = anchor.getBoundingClientRect();
  return {
    top: rect.bottom + MENU_OFFSET_PX,
    right: Math.max(0, window.innerWidth - rect.right),
  };
}

/**
 * Portals a header menu to document.body so overflow clipping and iOS
 * backdrop-filter stacking cannot hide it under the page.
 *
 * Placement matches in-header `absolute right-0 mt-2`.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {React.RefObject<HTMLElement | null>} props.anchorRef
 * @param {() => void} props.onClose
 * @param {string} [props.role]
 * @param {string} [props["aria-label"]]
 * @param {string} [props.className]
 * @param {React.ReactNode} props.children
 * @returns {React.ReactPortal | null}
 */
function HeaderDropdownPortal({
  open,
  anchorRef,
  onClose,
  role,
  "aria-label": ariaLabel,
  className,
  children,
}) {
  const menuRef = useRef(null);
  const [coords, setCoords] = useState(null);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return undefined;
    }

    function updatePosition() {
      setCoords(menuCoordsForAnchor(anchorRef.current));
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener("resize", updatePosition);
    visualViewport?.addEventListener("scroll", updatePosition);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      visualViewport?.removeEventListener("resize", updatePosition);
      visualViewport?.removeEventListener("scroll", updatePosition);
    };
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    function handlePointerDown(event) {
      const target = event.target;
      if (menuRef.current?.contains(target)) {
        return;
      }
      if (anchorRef.current?.contains(target)) {
        return;
      }
      onClose();
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose, anchorRef]);

  if (!open || !coords || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      ref={menuRef}
      role={role}
      aria-label={ariaLabel}
      className={cn("fixed z-50", className)}
      style={{ top: coords.top, right: coords.right }}
    >
      {children}
    </div>,
    document.body
  );
}

export default HeaderDropdownPortal;
